import { InvoiceSoftReminderStatus, InvoiceStatus } from '@constants'
import { IBusiness, IInvoice, IPostDueScheduleItem, PostDueReminderMark } from '@query'
import { isEligibleForPostDueAutomation } from '@root/utils'
import { isOverdue } from '@root/utils/invoicingHelpers'
import dayjs from 'dayjs'

export type ReminderMark = PostDueReminderMark

export const postDueFromInvoice = (invoice: IInvoice | undefined) => ({
  reminderMark: invoice?.post_due?.reminder_mark ?? null,
  kraviaMark: Boolean(invoice?.post_due?.kravia_registered),
  schedule: invoice?.post_due?.schedule ?? []
})

export const visiblePostDueSchedule = (
  invoice: IInvoice | undefined,
  {
    kraviaFeature,
    manualCollectionAvailable,
    automaticCollectionEnabled
  }: {
    kraviaFeature: boolean
    manualCollectionAvailable: boolean
    automaticCollectionEnabled: boolean
  }
): IPostDueScheduleItem[] => {
  const schedule = postDueFromInvoice(invoice).schedule
  if (!kraviaFeature) return schedule.filter(item => item.kind !== 'collection')
  if (
    !invoice ||
    !manualCollectionAvailable ||
    automaticCollectionEnabled ||
    invoice.is_registered_to_kravia ||
    !invoice.last_delivery_at ||
    invoice.is_credit_note_for ||
    !isOverdue(invoice) ||
    schedule.some(item => item.kind === 'collection')
  ) {
    return schedule
  }

  return [
    ...schedule,
    {
      kind: 'collection',
      state: dayjs(invoice.earliest_debt_collection_date).isAfter(dayjs(), 'day')
        ? 'waiting'
        : 'ready',
      at: invoice.earliest_debt_collection_date
    }
  ]
}

export const isPostDueCollectionServiceAvailable = (
  business: IBusiness | undefined,
  kraviaEnabled: boolean
) =>
  kraviaEnabled &&
  Boolean(business?.kravia_enabled && business?.is_automatic_debt_collection_enabled)

const isPostDueCollectionConfigKnown = (business: IBusiness) =>
  typeof business.kravia_enabled === 'boolean' &&
  typeof business.is_automatic_debt_collection_enabled === 'boolean'

export const shouldShowPostDueAutomationDetailAlert = (
  invoice: IInvoice,
  business: IBusiness | undefined | null
) => {
  if (invoice.is_credit_note_for) {
    return false
  }
  if (invoice.status === InvoiceStatus.PAID || invoice.status === InvoiceStatus.CREDIT_LOSS) {
    return false
  }
  return Boolean(business)
}

export const showKraviaDebtCollectionCheckbox = ({
  kraviaEnabled,
  business,
  invoiceDebtCollectionEnabled
}: {
  kraviaEnabled: boolean
  business: IBusiness | undefined
  invoiceDebtCollectionEnabled: boolean
}) => invoiceDebtCollectionEnabled || isPostDueCollectionServiceAvailable(business, kraviaEnabled)

export const postDueAutomationInactiveState = (
  invoice: IInvoice,
  business: IBusiness | undefined | null,
  kraviaEnabled: boolean
) => {
  if (!business) {
    return { reminderInactive: false, collectionInactive: false }
  }

  const reminderSteps = business.soft_reminder_steps
  const reminderInactive =
    Array.isArray(reminderSteps) &&
    invoice.is_automatic_soft_reminder_enabled &&
    reminderSteps.length === 0

  const collectionInactive =
    isPostDueCollectionConfigKnown(business) &&
    invoice.is_automatic_debt_collection_enabled &&
    !isPostDueCollectionServiceAvailable(business, kraviaEnabled)

  return { reminderInactive, collectionInactive }
}

export const postDueAutomationOffState = (
  invoice: IInvoice,
  business: IBusiness | undefined | null,
  kraviaEnabled: boolean
) => {
  if (!business) {
    return { reminderOff: false, collectionOff: false }
  }

  const eligible = isEligibleForPostDueAutomation(invoice)
  const reminderSteps = business.soft_reminder_steps
  const reminderOff =
    eligible &&
    Array.isArray(reminderSteps) &&
    reminderSteps.length > 0 &&
    !invoice.is_automatic_soft_reminder_enabled

  const collectionOff =
    eligible &&
    isPostDueCollectionConfigKnown(business) &&
    isPostDueCollectionServiceAvailable(business, kraviaEnabled) &&
    !invoice.is_automatic_debt_collection_enabled

  return { reminderOff, collectionOff }
}

export const isUpcomingWatchSchedule = (invoice: IInvoice | undefined) => {
  if (!invoice?.is_automatic_soft_reminder_enabled) return false
  if (invoice.is_past_due) return false
  if (invoice.automatic_soft_reminder_status !== InvoiceSoftReminderStatus.NOT_STARTED) {
    return false
  }
  return (invoice.post_due?.schedule ?? []).some(item => item.kind === 'reminder')
}
